import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import propertyService from '../../../services/propertyService';
import { stripeService } from '../../../services/stripe';
import usageTrackingService from '../../../services/usageTracking';
import { calculateMortgagePayment } from '../../../utils/mortgageCalculations';
import DataValidationAlert, { validatePropertyData } from './DataValidationAlert';

const AIAnalysisSummary = ({ property, user, userProfile, subscriptionTier = 'free', compact = false, summaryMode = false, onEdit, analysis: externalAnalysis, isReadOnly = false }) => {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(externalAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(!externalAnalysis);
  const [error, setError] = useState(null);
  const [isCached, setIsCached] = useState(!!externalAnalysis);
  const [usageStats, setUsageStats] = useState(null);

  // Update analysis if external analysis changes
  useEffect(() => {
    if (externalAnalysis) {
      setAnalysis(externalAnalysis);
      setIsCached(true);
      setLoadingExisting(false);
    }
  }, [externalAnalysis]);

  // Load existing analysis on mount (only if not provided externally)
  useEffect(() => {
    const loadExistingAnalysis = async () => {
      // Skip if analysis was provided externally or in readonly mode without user
      if (externalAnalysis || isReadOnly || !property?.id || !user?.id) {
        setLoadingExisting(false);
        return;
      }

      try {
        const result = await propertyService.getPropertyAIAnalysis(property.id, user.id);
        if (result?.success && result.data) {
          setAnalysis(result.data);
          setIsCached(true);
        }
      } catch (err) {
        // Silently handle errors
      } finally {
        setLoadingExisting(false);
      }
    };

    loadExistingAnalysis();
  }, [property?.id, user?.id, externalAnalysis, isReadOnly]);

  // Load usage stats
  useEffect(() => {
    const loadUsageStats = async () => {
      if (!user?.id) return;
      const stats = await usageTrackingService.getUsageStats(user.id, subscriptionTier);
      setUsageStats(stats);
    };
    loadUsageStats();
  }, [user?.id, subscriptionTier]);

  const generateAnalysis = async (forceRegenerate = false) => {
    if (!property || !user?.id) return;

    // Check AI analysis limit
    const limits = stripeService?.getTierLimits(subscriptionTier);
    if (limits?.aiAnalysesPerMonth !== Infinity && limits?.aiAnalysesPerMonth !== undefined) {
      if (usageStats?.aiAnalyses && usageStats.aiAnalyses.used >= limits.aiAnalysesPerMonth) {
        setError(`Vous avez atteint la limite de ${limits.aiAnalysesPerMonth} analyse${limits.aiAnalysesPerMonth > 1 ? 's' : ''} IA ce mois.`);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const formData = propertyService.toFormFormat(property);
      const metrics = propertyService.calculateMetrics(formData);

      const totalMonthlyRevenue = property?.total_monthly_revenue || 0;
      const totalMonthlyExpenses = property?.total_monthly_expenses || 0;
      const monthlyCashflow = metrics.monthlyCashflow || 0;
      const annualCashflow = monthlyCashflow * 12;
      const annualNOI = metrics.noi || 0;
      const capRate = metrics.capRate || 0;
      const totalInvestment = metrics.totalInitialInvestment || property?.down_payment_amount || 0;
      const cashOnCashReturn = totalInvestment > 0 ? (annualCashflow / totalInvestment) * 100 : 0;
      
      // Calculate annual mortgage payment using utility function (handles Canadian semi-annual compounding)
      const mortgageAmount = property?.mortgage_amount || 0;
      const interestRate = property?.interest_rate || 0;
      const amortizationYears = property?.amortization_period || 25;
      const paymentFrequency = property?.payment_frequency || 'monthly';
      
      const mortgagePaymentCalc = calculateMortgagePayment(
        mortgageAmount,
        interestRate,
        amortizationYears,
        paymentFrequency
      );
      const annualMortgagePayment = mortgagePaymentCalc.annualPayment;
      const dscr = annualMortgagePayment > 0 ? (annualNOI / annualMortgagePayment) : 0;

      const appreciationRate = property?.appreciation_rate || 3;
      const rentGrowth = (property?.rent_growth_rate || 2) / 100;
      const expenseGrowth = 0.02;
      const baseRevenueAnnual = totalMonthlyRevenue * 12;
      const baseExpenses = totalMonthlyExpenses * 12;

      const cumulativeCashflow10Years = Array.from({ length: 10 }, (_, j) => {
        const yRevenue = baseRevenueAnnual * Math.pow(1 + rentGrowth, j);
        const yExpenses = baseExpenses * Math.pow(1 + expenseGrowth, j);
        const yNOI = yRevenue - yExpenses;
        // Mortgage payment stops after amortization period
        const yActualMortgagePayment = ((j + 1) > amortizationYears) ? 0 : annualMortgagePayment;
        return yNOI - yActualMortgagePayment;
      }).reduce((sum, cf) => sum + (cf || 0), 0);

      const futureValue = (property?.purchase_price || 0) * Math.pow(1 + appreciationRate / 100, 10);
      const equityGain = futureValue - (property?.purchase_price || 0);
      const roi10Year = totalInvestment > 0 
        ? ((cumulativeCashflow10Years + equityGain) / totalInvestment) * 100 
        : 0;

      // Validate property data and include validation results
      const { missingFields, warnings } = validatePropertyData(property);
      const validationIssues = {
        critical: missingFields.map(f => f.label),
        warnings: warnings.map(f => f.label)
      };

      const propertyDataForAI = {
        ...property,
        ...metrics,
        totalMonthlyRevenue,
        totalMonthlyExpenses,
        monthlyCashflow,
        annualCashflow,
        noi: annualNOI,
        capRate,
        cashOnCashReturn,
        dscr,
        roi10Year,
        validationIssues // Include validation issues for AI to mention
      };

      const result = await propertyService.analyzePropertyAI(
        propertyDataForAI, 
        user.id, 
        property.id, // Always pass propertyId so it gets saved and counted
        forceRegenerate, // Pass forceRegenerate flag to create new entry
        userProfile // Pass investor profile for personalized analysis
      );

      if (result?.success) {
        setAnalysis(result.data);
        setIsCached(result.cached || false);
        
        // Check if save failed (result might have saveError property)
        if (result?.saveError) {
          console.warn('Analysis generated but save failed:', result.saveError);
          setError('L\'analyse a été générée mais n\'a pas pu être sauvegardée. Veuillez réessayer.');
        }
        
        // Always refresh usage stats after generating analysis (especially important for regeneration)
        // Add a small delay to ensure database has updated
        if (user?.id) {
          setTimeout(async () => {
            const stats = await usageTrackingService.getUsageStats(user.id);
            setUsageStats(stats);
          }, 500); // 500ms delay to allow database to update
        }
      } else {
        setError(result?.error || 'Erreur lors de l\'analyse');
      }
    } catch (err) {
      console.error('Error generating AI analysis:', err);
      setError(err?.message || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  // If compact mode (for navigation), handle early returns
  if (compact) {
    if (loadingExisting) {
      return (
        <Button
          variant="outline"
          size="sm"
          iconName="Loader2"
          disabled
        >
          Chargement...
        </Button>
      );
    }
    if (!analysis && !loading && !error) {
      return (
        <Button
          variant="outline"
          size="sm"
          iconName="Sparkles"
          onClick={() => generateAnalysis(false)}
          disabled={usageStats?.aiAnalyses && typeof usageStats.aiAnalyses.limit === 'number' && usageStats.aiAnalyses.used >= usageStats.aiAnalyses.limit}
        >
          Générer par l'IA
        </Button>
      );
    }
    if (loading) {
      return (
        <Button
          variant="outline"
          size="sm"
          iconName="Loader2"
          disabled
        >
          Génération...
        </Button>
      );
    }
    if (error) {
      return (
        <Button
          variant="outline"
          size="sm"
          iconName="AlertCircle"
          onClick={() => generateAnalysis(false)}
        >
          Réessayer
        </Button>
      );
    }
    return null; // When analysis exists in compact mode, don't show anything
  }

  // Full summary card for overview content
  if (loadingExisting) {
    return (
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-2">
          <Icon name="Loader2" size={16} className="animate-spin text-muted-foreground" />
          <span className="caption text-muted-foreground">Chargement de l'analyse IA...</span>
        </div>
      </div>
    );
  }

  if (!analysis && !loading && !error) {
    return (
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="Sparkles" size={18} style={{ color: 'var(--color-primary)' }} />
            <span className="caption text-muted-foreground">Aucune analyse IA disponible</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Sparkles"
            onClick={() => generateAnalysis(false)}
            disabled={usageStats?.aiAnalyses && typeof usageStats.aiAnalyses.limit === 'number' && usageStats.aiAnalyses.used >= usageStats.aiAnalyses.limit}
          >
            Générer
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center gap-2">
          <Icon name="Loader2" size={16} className="animate-spin text-primary" />
          <span className="caption text-muted-foreground">Génération en cours...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={18} style={{ color: 'var(--color-destructive)' }} />
            <span className="caption text-destructive">{error}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateAnalysis(false)}
          >
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  const getRecommendationColor = (niveau) => {
    if (!niveau) return 'var(--color-muted-foreground)';
    const niveauUpper = niveau.toUpperCase();
    if (niveauUpper.includes('EXCELLENT')) return { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', badge: 'bg-success' };
    if (niveauUpper.includes('BON')) return { bg: 'bg-primary/10', border: 'border-primary/30', text: 'text-primary', badge: 'bg-primary' };
    if (niveauUpper.includes('MOYEN')) return { bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning', badge: 'bg-warning' };
    if (niveauUpper.includes('FAIBLE')) return { bg: 'bg-destructive/10', border: 'border-destructive/30', text: 'text-destructive', badge: 'bg-destructive' };
    return { bg: 'bg-muted', border: 'border-border', text: 'text-muted-foreground', badge: 'bg-muted' };
  };

  const recommendationColors = analysis?.recommandation?.niveau 
    ? getRecommendationColor(analysis.recommandation.niveau)
    : { bg: 'bg-muted', border: 'border-border', text: 'text-muted-foreground', badge: 'bg-muted' };

  // Summary mode: shows just the executive summary in a compact format at the top
  if (summaryMode && analysis) {
    return (
      <div className={`${recommendationColors.bg} ${recommendationColors.border} border rounded-xl p-4`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={20} style={{ color: 'var(--color-primary)' }} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-foreground">Analyse IA</h3>
                {analysis?.recommandation?.niveau && (
                  <span className={`${recommendationColors.badge} text-white px-2 py-0.5 rounded-full text-xs font-semibold`}>
                    {analysis.recommandation.niveau}
                  </span>
                )}
              </div>
              {!isReadOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => generateAnalysis(true)}
                />
              )}
            </div>
            <p className="text-sm leading-relaxed text-foreground">
              {analysis.resume_executif || analysis?.recommandation?.justification || 'Analyse en cours...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Summary mode without analysis yet - show generate button (only if not readonly)
  if (summaryMode && !analysis && !isReadOnly) {
    return (
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon name="Sparkles" size={20} style={{ color: 'var(--color-primary)' }} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Analyse IA</h3>
              <p className="text-sm text-muted-foreground">
                {loading ? 'Génération en cours...' : 'Obtenez une analyse personnalisée par IA'}
              </p>
            </div>
          </div>
          <Button
            variant="default"
            size="sm"
            iconName={loading ? "Loader2" : "Sparkles"}
            onClick={() => generateAnalysis(false)}
            disabled={loading || (usageStats?.aiAnalyses && typeof usageStats.aiAnalyses.limit === 'number' && usageStats.aiAnalyses.used >= usageStats.aiAnalyses.limit)}
          >
            {loading ? 'Génération...' : 'Générer'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Sparkles" size={18} style={{ color: 'var(--color-primary)' }} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">
              Analyse IA
            </h3>
            {isCached && (
              <span className="text-xs text-muted-foreground">Analyse enregistrée</span>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="RefreshCw"
          onClick={() => generateAnalysis(true)}
        >
          Régénérer
        </Button>
      </div>

      {/* Résumé exécutif */}
      {analysis?.resume_executif && (
        <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="FileText" size={20} style={{ color: 'var(--color-primary)' }} />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground mb-2">Résumé exécutif</h4>
              <p className="text-sm leading-relaxed text-foreground">
                {analysis.resume_executif}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Métriques visuelles */}
      <div className="p-4 space-y-4">
        {/* Recommandation - Badge principal */}
        {analysis?.recommandation?.niveau && (
          <div className={`${recommendationColors.bg} ${recommendationColors.border} border-2 rounded-xl p-4`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon name="Target" size={18} className={recommendationColors.text} />
                <h4 className="text-sm font-semibold text-foreground">Recommandation</h4>
              </div>
              <span className={`${recommendationColors.badge} text-white px-3 py-1 rounded-full text-xs font-semibold`}>
                {analysis.recommandation.niveau}
              </span>
            </div>
            {analysis.recommandation.justification && (
              <p className="text-sm text-foreground leading-relaxed mb-2">
                {analysis.recommandation.justification}
              </p>
            )}
            {analysis.recommandation.conditions && (
              <div className="mt-3 pt-3 border-t border-border/50">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Conditions / Améliorations suggérées:</p>
                <p className="text-sm text-foreground leading-relaxed">
                  {analysis.recommandation.conditions}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Grille de métriques */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Secteur */}
          {analysis?.secteur && (
            <div className="bg-muted/50 border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="MapPin" size={16} className="text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Secteur</h4>
              </div>
              <p className="text-xs text-foreground leading-relaxed line-clamp-3">
                {analysis.secteur.analyse}
              </p>
              {analysis.secteur.points_forts && analysis.secteur.points_forts.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1 mb-1">
                    <Icon name="CheckCircle2" size={12} className="text-success" />
                    <span className="text-xs font-semibold text-success">{analysis.secteur.points_forts.length} point{analysis.secteur.points_forts.length > 1 ? 's' : ''} fort{analysis.secteur.points_forts.length > 1 ? 's' : ''}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Prix */}
          {analysis?.prix && (
            <div className="bg-muted/50 border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={16} className="text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Évaluation du Prix</h4>
              </div>
              <p className="text-xs text-foreground leading-relaxed line-clamp-4">
                {analysis.prix.evaluation}
              </p>
            </div>
          )}

          {/* Rentabilité - Cashflow */}
          {analysis?.rentabilite?.cashflow && (
            <div className="bg-muted/50 border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-success" />
                <h4 className="text-sm font-semibold text-foreground">Cashflow</h4>
              </div>
              <p className="text-xs text-foreground leading-relaxed line-clamp-3">
                {analysis.rentabilite.cashflow}
              </p>
            </div>
          )}

          {/* Rentabilité - DSCR */}
          {analysis?.rentabilite?.dscr && (
            <div className="bg-muted/50 border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Shield" size={16} className="text-primary" />
                <h4 className="text-sm font-semibold text-foreground">DSCR</h4>
              </div>
              <p className="text-xs text-foreground leading-relaxed line-clamp-3">
                {analysis.rentabilite.dscr}
              </p>
            </div>
          )}
        </div>

        {/* ROI et CAP Rate */}
        {(analysis?.rentabilite?.roi || analysis?.rentabilite?.cap_rate) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {analysis.rentabilite.roi && (
              <div className="bg-muted/50 border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="BarChart3" size={16} className="text-primary" />
                  <h4 className="text-sm font-semibold text-foreground">ROI</h4>
                </div>
                <p className="text-xs text-foreground leading-relaxed line-clamp-3">
                  {analysis.rentabilite.roi}
                </p>
              </div>
            )}
            {analysis.rentabilite.cap_rate && (
              <div className="bg-muted/50 border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Percent" size={16} className="text-primary" />
                  <h4 className="text-sm font-semibold text-foreground">CAP Rate</h4>
                </div>
                <p className="text-xs text-foreground leading-relaxed line-clamp-3">
                  {analysis.rentabilite.cap_rate}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalysisSummary;
